import type { Program, ProgramExercise, ProgramWeek, ProgramDay } from '@/types'

/**
 * Spreadsheet → Program. Handles two shapes:
 *  1. Multi-tab "Session" format (Session A/B/C tabs, week columns "Wk 1".."Wk 4"
 *     or ranges "Wk 1-2"), the layout coaches build in Claude.
 *  2. Flat single-sheet (one exercise per row, Week/Day/Exercise/… columns).
 */

type ResolveId = (ex: { exercise_id?: string | null; name: string }) => string
export interface SheetData { name: string; rows: unknown[][] }

const norm = (v: unknown) => String(v ?? '').trim()
const lc = (v: unknown) => norm(v).toLowerCase()

function slug(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

function parseSetsReps(cell: string): { sets: number | null; reps: string | null } {
  const t = norm(cell)
  if (!t) return { sets: null, reps: null }
  const m = t.match(/^\s*(\d+)\s*[x×]\s*(.+?)\s*$/i)
  if (m) return { sets: Number(m[1]), reps: m[2].trim() }
  return { sets: null, reps: t }
}

const OMIT = /^(omit|skip|none|-|n\/a|as prescribed)$/i

function splitTempo(cue: string): { tempo: string | null; notes: string | null } {
  if (!cue) return { tempo: null, notes: null }
  const m = cue.match(/tempo\s*([0-9]+-[0-9]+-[0-9]+)/i)
  if (m) {
    const notes = cue.replace(/tempo\s*[0-9]+-[0-9]+-[0-9]+\.?\s*/i, '').trim()
    return { tempo: m[1], notes: notes || null }
  }
  return { tempo: null, notes: cue }
}

/** "Wk 1" → [1]; "Wk 3-5" → [3,4,5]; "W1"/other → null (not a prescription col). */
function weekCols(header: string): number[] | null {
  if (!/wk\s*\d/i.test(header)) return null
  const nums = (header.match(/\d+/g) || []).map(Number)
  if (!nums.length) return null
  if (nums.length >= 2) {
    const lo = Math.min(...nums), hi = Math.max(...nums)
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  }
  return [nums[0]]
}

function findCol(header: string[], test: RegExp) {
  return header.findIndex((h) => test.test(lc(h)))
}

// ── Multi-tab Session format ───────────────────────────────
function parseSessionFormat(sessions: SheetData[], opts: { name?: string; resolveId?: ResolveId }): Program {
  // week -> ordered day label -> ProgramDay
  const weekMap = new Map<number, Map<string, ProgramDay>>()
  const dayOrder: string[] = []

  for (const sheet of sessions) {
    const rows = sheet.rows
    const headerIdx = rows.findIndex(
      (r) => (r || []).some((c) => lc(c) === '#' || /^exercise$/.test(lc(c)))
    )
    if (headerIdx < 0) continue
    const header = (rows[headerIdx] || []).map(norm)

    const codeCol = header.findIndex((h) => h === '#')
    let exCol = findCol(header, /exercise|movement/)
    if (exCol < 0) exCol = codeCol >= 0 ? codeCol + 1 : 1
    const cueCol = findCol(header, /cue|coaching|tempo|standard/)
    const presc = header
      .map((h, i) => ({ i, weeks: weekCols(h) }))
      .filter((x): x is { i: number; weeks: number[] } => Array.isArray(x.weeks))

    const label = sheet.name.trim()
    if (!dayOrder.includes(label)) dayOrder.push(label)
    const titleCell = norm(rows[0]?.[0] as unknown)
    const focus = titleCell.includes('|') ? titleCell.split('|').slice(1).join('|').trim() : undefined

    const get = (row: unknown[], i: number) => (i >= 0 ? norm(row[i]) : '')

    for (let r = headerIdx + 1; r < rows.length; r++) {
      const row = rows[r] || []
      const name = get(row, exCol)
      if (!name || /^(pairings|from week\b|notes?:|legend|key\b)/i.test(name)) continue
      const code = get(row, codeCol) || null
      const { tempo, notes } = splitTempo(get(row, cueCol))

      for (const p of presc) {
        const cell = get(row, p.i)
        if (!cell || OMIT.test(cell)) continue
        const { sets, reps } = parseSetsReps(cell)
        const ex: ProgramExercise = { exercise_id: '', name, sets, reps, tempo, rest: null, notes, group: code, load: null }
        ex.exercise_id = opts.resolveId ? opts.resolveId(ex) : slug(name)
        for (const wk of p.weeks) {
          if (!weekMap.has(wk)) weekMap.set(wk, new Map())
          const days = weekMap.get(wk)!
          if (!days.has(label)) days.set(label, { label, focus, exercises: [] })
          days.get(label)!.exercises.push({ ...ex })
        }
      }
    }
  }

  const weeks: ProgramWeek[] = [...weekMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([week, days]) => ({
      week,
      days: dayOrder.filter((l) => days.has(l)).map((l) => days.get(l)!),
    }))

  return {
    id: 'imported-' + Date.now(),
    name: opts.name || 'Imported Program',
    source: 'import',
    weeks: weeks.length ? weeks : [{ week: 1, days: [{ label: 'Day 1', exercises: [] }] }],
  }
}

// ── Flat single-sheet format ───────────────────────────────
function parseFlat(rows: unknown[][], opts: { name?: string; resolveId?: ResolveId }): Program {
  let headerIdx = rows.findIndex((r) => (r || []).some((c) => /exercise|movement|^name$/.test(lc(c))))
  if (headerIdx < 0) headerIdx = 0
  const header = (rows[headerIdx] || []).map(norm)
  const col = {
    week: findCol(header, /week|wk/),
    day: findCol(header, /^day|session/),
    ex: findCol(header, /exercise|movement|^name$/),
    sets: findCol(header, /^set|^sets$/),
    reps: findCol(header, /^rep|^reps$/),
    setsReps: findCol(header, /sets?\s*[x×/]\s*reps?|prescription|scheme/),
    load: findCol(header, /load|weight|intensity|rpe|1rm|%/),
    tempo: findCol(header, /tempo/),
    rest: findCol(header, /rest/),
    notes: findCol(header, /note|cue|coaching|comment/),
    group: findCol(header, /group|superset|pairing|^#$/),
  }
  // Guard: a real exercise table needs an "exercise" header OR prescription
  // columns. Otherwise (e.g. an Overview / rules tab) produce nothing so we
  // don't turn prose rows into fake exercises.
  const exFound = col.ex >= 0
  const hasPrescCols = col.sets >= 0 || col.reps >= 0 || col.setsReps >= 0 || col.load >= 0
  if (!exFound && !hasPrescCols) {
    return {
      id: 'imported-' + Date.now(),
      name: opts.name || 'Imported Program',
      source: 'import',
      weeks: [{ week: 1, days: [{ label: 'Day 1', exercises: [] }] }],
    }
  }
  if (col.ex < 0) col.ex = 0
  const get = (row: unknown[], i: number) => (i >= 0 ? norm(row[i]) : '')

  const weeksMap = new Map<number, Map<string, ProgramExercise[]>>()
  let curWeek = 1
  let curDay = 'Day 1'
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] || []
    const name = get(row, col.ex)
    if (get(row, col.week)) {
      const n = parseInt(get(row, col.week).replace(/[^0-9]/g, ''), 10)
      if (!Number.isNaN(n)) curWeek = n
    }
    if (get(row, col.day)) curDay = get(row, col.day)
    if (!name) continue
    let sets: number | null = null
    let reps: string | null = null
    if (get(row, col.sets)) {
      const s = parseInt(get(row, col.sets), 10)
      if (!Number.isNaN(s)) sets = s
    }
    if (get(row, col.reps)) reps = get(row, col.reps)
    if ((sets === null || reps === null) && col.setsReps >= 0) {
      const p = parseSetsReps(get(row, col.setsReps))
      sets = sets ?? p.sets
      reps = reps ?? p.reps
    }
    const ex: ProgramExercise = {
      exercise_id: '', name, sets, reps,
      load: get(row, col.load) || null, tempo: get(row, col.tempo) || null,
      rest: get(row, col.rest) || null, notes: get(row, col.notes) || null,
      group: get(row, col.group) || null,
    }
    ex.exercise_id = opts.resolveId ? opts.resolveId(ex) : slug(name)
    if (!weeksMap.has(curWeek)) weeksMap.set(curWeek, new Map())
    const days = weeksMap.get(curWeek)!
    if (!days.has(curDay)) days.set(curDay, [])
    days.get(curDay)!.push(ex)
  }

  const weeks: ProgramWeek[] = [...weeksMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([week, days]) => ({ week, days: [...days.entries()].map(([label, exercises]) => ({ label, exercises })) }))

  return {
    id: 'imported-' + Date.now(),
    name: opts.name || 'Imported Program',
    source: 'import',
    weeks: weeks.length ? weeks : [{ week: 1, days: [{ label: 'Day 1', exercises: [] }] }],
  }
}

/** A training tab has a header row with an Exercise column AND week columns —
 *  regardless of what the tab is called ("Session A", "Day 1 - Max Strength", …). */
function isTrainingSheet(rows: unknown[][]): boolean {
  for (let i = 0; i < Math.min(rows.length, 12); i++) {
    const cells = (rows[i] || []).map((c) => lc(c))
    const hasEx = cells.some((c) => /exercise|movement/.test(c))
    const hasWk = cells.some((c) => /wk\s*\d/.test(c))
    if (hasEx && hasWk) return true
  }
  return false
}

/** Entry point — dispatches on workbook shape. */
export function parseWorkbook(sheets: SheetData[], opts: { name?: string; resolveId?: ResolveId } = {}): Program {
  // Prefer tabs detected by content (works for any tab naming).
  const training = sheets.filter((s) => isTrainingSheet(s.rows))
  if (training.length) return parseSessionFormat(training, opts)
  // Fallback: tabs named like session/day.
  const named = sheets.filter((s) => /session|^\s*day\b/i.test(s.name))
  if (named.length) return parseSessionFormat(named, opts)
  // Otherwise treat the first sheet as a flat one-exercise-per-row table.
  return parseFlat(sheets[0]?.rows ?? [], opts)
}

export function countExercises(p: Program): number {
  return p.weeks.reduce((n, w) => n + w.days.reduce((m, d) => m + d.exercises.length, 0), 0)
}
